// 要素式文书：页面间共享状态与对话助手。
// flowInput 为首页（index.vue）→ 生成中/结果页（generateResults.vue）的传参快照，
// 模块级 ref 保持响应（直接访问/刷新后丢失，由 generateResults 回落示例案例兜底，
// 兜底文案 EXAMPLE_CASE 已移至 @/api/elementBased）
import { ref } from "vue";
import * as signalR from "@microsoft/signalr";
import {
  addElementChat,
  getMyElementChatsPage,
  addElementChatHistory,
  getSnowflakeId,
} from "@/api/elementBased";
import { getApiBaseUrl } from "@/utils/env";

// 生成流程入参快照：
// { text, files: [{ name, label, size }], sourceType: text|material|complaint,
//   materials: [上传返回的会话实体 { id, fileName, fullText, fileDownloadUrl, sourceText }] }
export const flowInput = ref(null);

// 要素式文书专用智能体 appId（后端分配）
export const ELEMENT_APP_ID = "9700000201";

// IndictmentConversation 会话 id：上传接口返回（startFlow 写入）或记录页查看时
// 经路由 query.id 带入；创建聊天（AIChats/Add）时随入参上送关联
export const elementConversationId = ref(null);

// 当前对话 id（aIChatsId）：进入聊天页（GenerateFlow 挂载）即建好；
// 「查看」直达结果页时为空，AiChatSide 首次发消息时懒建
export const elementChatId = ref(null);

// 建会话：Add 只调一次，目的就是获取 aIChatsId（会话 id）。
// 默认参数（与智能体「新对话」一致）；优先取 Add 返回的 aIChatsId，
// 未带回时兜底回查列表取最新一条；没有 aIChatsId 不能开始消息
export const initElementChat = async () => {
  if (elementChatId.value) return elementChatId.value;
  const res = await addElementChat({
    appId: ELEMENT_APP_ID,
    name: "新对话",
    lastMessage: "",
  });
  const chatId =
    res?.data?.aiChatsId ?? res?.data?.aIChatsId ?? res?.data?.id ?? null;
  if (chatId) {
    elementChatId.value = chatId;
    return chatId;
  }
  const page = await getMyElementChatsPage({ pageNum: 1, pageSize: 1 });
  elementChatId.value = page?.data?.data?.[0]?.id ?? null;
  return elementChatId.value;
};

// 新流程重置（GenerateFlow 每次挂载调用）：下个流程重新建会话
export const resetElementChat = () => {
  elementChatId.value = null;
};

// 发一条消息给 AI，返回 AI 回复记录（含 content）；
// aIChatsId 为 AIChats/Add 返回的会话 id，id 为本条消息的雪花 id
export const sendElementMessage = async (content, fileNames = "") => {
  const chatId = elementChatId.value ?? (await initElementChat());
  if (!chatId) throw new Error("创建对话失败");
  const snowflakeId = (await getSnowflakeId()).data;
  const res = await addElementChatHistory({
    aIChatsId: chatId,
    id: snowflakeId,
    content,
    isOnlineSearch: false,
    fileNames,
    contentFileUrls: "",
  });
  return res?.data;
};

// 发一条消息给 AI（流式版，机制与 MyAIChat 相同）：
// Add 的 HTTP 响应（application/json）只带最终完整记录——增量经 SignalR
// hub 推送（aimsg 事件）。先按本条消息的雪花 id 建好 hub 连接
// （IdentityId 绑定），再 POST Add（该请求等 AI 完整回复后才返回，
// 返回体为最终 AI 回复记录），结束后关连接。
// onChunk 每收到一段增量文本（aimsg 事件）回调一次；
// 返回值为 Add 返回的最终回复记录（含 content）
export const sendElementMessageStream = async (
  content,
  fileNames = "",
  { onChunk, signal } = {},
) => {
  const chatId = elementChatId.value ?? (await initElementChat());
  if (!chatId) throw new Error("创建对话失败");
  const snowflakeId = (await getSnowflakeId()).data;

  // SignalR 流式通道：IdentityId 用本条消息的雪花 id；
  // 后端没有 negotiate 接口，skipNegotiation 直连 WebSocket
  let connection = null;
  // TODO 调试用：定位流式问题后移除
  let chunkCount = 0;
  try {
    const token = localStorage.getItem("token");
    connection = new signalR.HubConnectionBuilder()
      .withUrl(
        // getApiBaseUrl() 带尾斜杠，这里不能再加开头斜杠（会变 // 导致 404）
        `${getApiBaseUrl()}api/MySignalRHub?IdentityId=${snowflakeId}&Authorization=${token}`,
        {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        },
      )
      .withAutomaticReconnect()
      .build();
    // TODO 调试用：定位流式问题后移除这三条 console
    connection.on("aimsg", (msg) => {
      chunkCount++;
      if (onChunk) onChunk(msg);
    });
    await connection.start();
    console.log("[ElementBased] hub 已连接, IdentityId =", snowflakeId);
  } catch (err) {
    // hub 连不上不阻塞发送：Add 返回的完整回复仍可用
    console.error("[ElementBased] SignalR 连接失败:", err);
    connection?.stop?.();
    connection = null;
  }

  try {
    const res = await addElementChatHistory(
      {
        aIChatsId: chatId,
        id: snowflakeId,
        content,
        isOnlineSearch: false,
        fileNames,
        contentFileUrls: "",
      },
      signal,
    );
    // TODO 调试用：0 = 后端没推 aimsg（或没连上）；>0 = 推了
    console.log(
      "[ElementBased] Add 完成, aimsg 段数 =",
      chunkCount,
      ", 返回 content 长度 =",
      res?.data?.content?.length ?? 0,
    );
    return res?.data;
  } finally {
    if (connection) connection.stop().catch(() => {});
  }
};
